import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import maskEmail from '../../components/common/MaskEmail';
import { isLoggedIn } from '../../components/authProvider/AuthContext';
import PrimaryButton from '../../components/common/button/PrimaryButton';
import { Api } from '../../redux-state/userAuth/api';

const SERVER_URL = process.env.REACT_APP_API_URL;

function VerifyUser() {
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();
  const inputRefs = useRef([]);
  const username = localStorage.getItem('email');

  useEffect(() => {
    if (isLoggedIn()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleChange = (e, index) => {
    const inputValue = e.target.value.replace(/[^0-9]/g, '');
    if (!inputValue) return;

    const newOtp = [...otp];
    newOtp[index] = inputValue[0];
    setOtp(newOtp);

    if (index < 5 && inputValue) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);

    if (!paste.length) return;

    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = paste[i] || '';
    }

    setOtp(newOtp);
    inputRefs.current[Math.min(paste.length - 1, 5)]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];

      if (otp[index]) {
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const combinedOtp = otp.join('');

    const formData = {
      verification_code: combinedOtp,
    };

    try {
      const csrfToken = await Api.getCsrfToken();
      const response = await axios.post(`${SERVER_URL}/api/verifyUserV2`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRFToken': csrfToken,
        },
        withCredentials: true,
      });

      if (response.status === 200) {
        setSuccess(response.data.message);
        setLoading(false);
        navigate('/login');
      } else {
        setError(response.data.message);
        setOtp(new Array(6).fill(''));
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during verification';
      setError(errorMessage);
      setOtp(new Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const csrfToken = await Api.getCsrfToken();
      const response = await axios.post(`${SERVER_URL}/api/resendVerificationV2`, {}, {
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      if (response.data.status === 200) {
        setSuccess(response.data.message || 'Verification code resent successfully.');
      } else {
        setError(response.data.message || 'Failed to resend verification code.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to resend verification code.';
      setError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <form className="bg-white rounded-2xl shadow-md p-10 min-w-[30%] w-[500px]" onSubmit={handleSubmit}>
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-12">
            <img className="h-14 w-14" src="/logo.png" alt="logo" />
            <span className="ml-3 text-lg font-semibold text-gray-800">DMV Navigator</span>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verification</h2>
          <p className="text-gray-600 text-sm">
            Please enter the verification code sent to{' '}
            <span className="font-medium">{maskEmail(username)}</span>
          </p>
        </div>

        <div className="mb-6">
          <div className="flex justify-center gap-2 mb-4">
            {otp.map((value, index) => (
              <input
                key={index}
                ref={(el) => inputRefs.current[index] = el}
                type="text"
                maxLength="1"
                value={value}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                className="w-12 h-12 text-center text-xl font-semibold border-2 border-gray-300 rounded-md focus:outline-none focus:border-blue-500 transition-colors"
              />
            ))}
          </div>

          {error && <div className="text-center text-sm text-red-500 mb-2">{error}</div>}
          {success && <div className="text-center text-sm text-green-500 mb-2">{success}</div>}

          <div className="text-center">
            <span className="text-gray-600 text-sm">Didn't receive the verification code? </span>
            <button
              type="button"
              className="text-green-800 text-sm font-medium hover:text-green-900 hover:underline"
              onClick={handleResendCode}
              disabled={resendLoading}
            >
              {resendLoading ? 'Resending...' : 'Resend Code?'}
            </button>
          </div>
        </div>

        <PrimaryButton
          text={loading ? 'Verifying...' : 'Verify'}
          type="submit"
          disabled={loading}
          className="w-full"
        />

        <div className="text-center mt-8">
          <button
            type="button"
            className="text-gray-600 text-sm hover:text-gray-800 hover:underline transition-colors"
            onClick={handleBackToLogin}
          >
            Back to Login
          </button>
        </div>
      </form>
    </div>
  );
}

export default VerifyUser;
